<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import TextAreaInput from "@/components/Inputs/TextAreaInput.vue";
import { useShortsStore } from "@/stores/modules/shorts/shorts";
import { useShortUploadStore } from "@/stores/modules/shorts/short-upload";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useQuotaStore } from "@/stores/modules/quota/quota";
import { setWarningToast } from "@/helpers/notifications/toast";
import { DEFAULT_SHORT_MAX_DURATION_SECONDS } from "@/constants/shorts";
import {
  extractShortPosterAndMetadata,
  type ShortExtractedMetadata,
} from "@/helpers/shorts/short-metadata";

const { t } = useI18n();
const shortsStore = useShortsStore();
const shortUploadStore = useShortUploadStore();
const groupsStore = useGroupsStore();
const authStore = useAuthStore();
const quotaStore = useQuotaStore();

const caption = ref("");
const videoPreviewUrl = ref<string | null>(null);

let pendingMetadataPromise: Promise<ShortExtractedMetadata> | null = null;
let currentMetadataResult: ShortExtractedMetadata | null = null;

const isOpen = computed({
  get: () =>
    shortsStore.isTransferringFromNative || !!shortsStore.recordedVideoFile,
  set: (val: boolean) => {
    if (!val) {
      discardShort();
    }
  },
});

watch(
  () => shortsStore.recordedVideoFile,
  (file) => {
    // Clean up previous preview & metadata
    if (videoPreviewUrl.value) {
      URL.revokeObjectURL(videoPreviewUrl.value);
      videoPreviewUrl.value = null;
    }
    if (currentMetadataResult) {
      currentMetadataResult.cleanup();
      currentMetadataResult = null;
    }
    pendingMetadataPromise = null;

    if (file) {
      videoPreviewUrl.value = URL.createObjectURL(file);
      caption.value = "";

      // Immediately pre-compute thumbnail, blurhash, and duration in parallel
      pendingMetadataPromise = extractShortPosterAndMetadata(file);
      pendingMetadataPromise
        .then((res) => {
          currentMetadataResult = res;
        })
        .catch((err) => {
          console.warn("Pre-computation of short metadata failed:", err);
        });
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  if (videoPreviewUrl.value) {
    URL.revokeObjectURL(videoPreviewUrl.value);
  }
  if (currentMetadataResult) {
    currentMetadataResult.cleanup();
  }
});

function discardShort() {
  if (shortsStore.isTransferringFromNative) {
    shortsStore.abortTransfer();
  }
  if (videoPreviewUrl.value) {
    URL.revokeObjectURL(videoPreviewUrl.value);
    videoPreviewUrl.value = null;
  }
  if (currentMetadataResult) {
    currentMetadataResult.cleanup();
    currentMetadataResult = null;
  }
  pendingMetadataPromise = null;
  caption.value = "";
  shortsStore.setRecordedVideoFile(null);
}

function submitShort() {
  const file = shortsStore.recordedVideoFile;
  const groupId = groupsStore.groupData?._id;
  const authUuid = authStore.authUuid;

  if (!file || !groupId || !authUuid) {
    return;
  }

  if (quotaStore.isShortQuotaExceeded) {
    setWarningToast({
      message: t("pages.setting.quota.short_limit_reached"),
    });
    return;
  }

  const captionText = caption.value;
  const metadataPromise = pendingMetadataPromise || undefined;

  // Hand off the upload job to the background store
  shortUploadStore.startUpload({
    file,
    caption: captionText,
    groupId,
    authUuid,
    precomputedMetadataPromise: metadataPromise,
  });

  // Transfer ownership of metadata cleanup to shortUploadStore, prevent local cleanup
  currentMetadataResult = null;
  pendingMetadataPromise = null;

  // Close the bottom sheet immediately so the user can continue browsing
  discardShort();
}
</script>

<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="yellow"
    padding="normal"
    initial-height="full"
  >
    <div class="dark:text-slate-200">
      <!-- Header -->
      <div class="mb-6 flex items-start justify-between gap-4">
        <h2 class="flex-1 text-2xl font-bold">
          {{ $t("pages.shorts.add_title") }}
        </h2>
        <default-button
          color="yellow"
          :disabled="shortsStore.isTransferringFromNative"
          @click="submitShort"
        >
          {{ $t("pages.shorts.post_button") }}
        </default-button>
      </div>

      <!-- Video Preview Card -->
      <div
        class="relative mb-4 flex min-h-64 justify-center overflow-hidden rounded-2xl border-2 border-black-dark bg-black-dark shadow-teal-hard-sm"
      >
        <!-- Transferring Loader State -->
        <div
          v-if="shortsStore.isTransferringFromNative"
          class="flex flex-col items-center justify-center gap-3 p-8 text-center text-white"
        >
          <div class="relative flex h-16 w-16 items-center justify-center">
            <span
              class="absolute inline-flex h-full w-full animate-ping rounded-full bg-kekkle-yellow opacity-40"
            ></span>
            <div
              class="relative flex h-14 w-14 items-center justify-center rounded-full border-2 border-black-dark bg-kekkle-yellow text-2xl text-black-dark shadow-sm"
            >
              🎬
            </div>
          </div>
          <div>
            <p class="text-sm font-bold text-gray-200">
              {{ $t("pages.shorts.processing_video") }}
            </p>
            <p class="text-xs font-semibold text-kekkle-yellow">
              {{ Math.round(shortsStore.nativeTransferProgress * 100) }}%
            </p>
          </div>
          <!-- Progress bar -->
          <div
            class="w-44 overflow-hidden rounded-full border border-black-dark bg-white/20 p-0.5"
          >
            <div
              class="h-2 rounded-full bg-kekkle-yellow transition-all duration-150"
              :style="{
                width: `${Math.round(shortsStore.nativeTransferProgress * 100)}%`,
              }"
            ></div>
          </div>
        </div>

        <!-- Video Player Preview -->
        <video
          v-else-if="videoPreviewUrl"
          :src="videoPreviewUrl"
          playsinline
          autoplay
          loop
          muted
          class="w-auto object-contain"
        />

        <div
          v-if="!shortsStore.isTransferringFromNative"
          class="absolute top-3 right-3 rounded-full bg-black-dark/60 px-2.5 py-1 text-xs font-bold text-white backdrop-blur-xs"
        >
          ⏱️ {{ DEFAULT_SHORT_MAX_DURATION_SECONDS }}s max
        </div>
      </div>

      <!-- Quota Indicator Banner -->
      <div
        v-if="quotaStore.shortQuota"
        class="mb-4 flex items-center justify-between rounded-xl border-2 border-black-dark bg-kekkle-yellow/20 px-3.5 py-2.5 text-xs font-bold text-gray-800 dark:bg-kekkle-yellow/10 dark:text-gray-200"
      >
        <div class="flex items-center gap-1.5">
          <span class="text-sm">🎬</span>
          <span>{{ $t("pages.setting.quota.short_usage") }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span
            :class="
              quotaStore.isShortQuotaExceeded
                ? 'font-black text-kekkle-red'
                : 'text-gray-700 dark:text-gray-300'
            "
          >
            {{ quotaStore.shortQuota.daily_count }} /
            {{ quotaStore.shortQuota.daily_limit }}
            {{ $t("pages.setting.quota.daily").toLowerCase() }}
          </span>
          <span class="text-gray-400">•</span>
          <span class="text-gray-500 dark:text-gray-400">
            {{ quotaStore.shortQuota.total_count }} /
            {{ quotaStore.shortQuota.total_limit }}
            {{ $t("pages.setting.quota.total").toLowerCase() }}
          </span>
        </div>
      </div>

      <!-- Caption Input -->
      <div class="mb-4">
        <text-area-input
          v-model="caption"
          :label="$t('pages.shorts.caption_label')"
          :placeholder="$t('pages.shorts.caption_placeholder')"
          :max-length="350"
        />
        <div
          class="mt-1 flex justify-end text-xs text-gray-500 dark:text-gray-400"
        >
          {{ caption.length }} / 350
        </div>
      </div>

      <!-- Discard Option -->
      <div class="mt-4 flex justify-center">
        <default-button color="red" size="small" @click="discardShort">
          🗑️ {{ $t("pages.shorts.discard") }}
        </default-button>
      </div>
    </div>
  </KekkleBottomSheet>
</template>
